import type React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Home,
  Database,
  FileText,
  Layout,
  Settings,
  Crown,
  PlusCircle,
  FileBarChart,
  HelpCircle,
  Sparkles,
  Library
} from 'lucide-react'
import { useSubscription } from '../../contexts/SubscriptionContext'
import { useAuth } from '../../contexts/AuthContext'

export interface SidebarItem {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  path?: string
  badge?: number
  premium?: boolean
  adminOnly?: boolean
  children?: Omit<SidebarItem, 'children' | 'badge' | 'premium' | 'adminOnly'>[]
}

export interface SidebarGroup {
  id: string
  label: string
  items: SidebarItem[]
}

export const sidebarGroups: SidebarGroup[] = [
  {
    id: 'criar',
    label: 'Criar',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/app' },
      { id: 'my_assessments', label: 'Minhas Avaliações', icon: FileText, path: '/app/assessments' },
      {
        id: 'create_assessment_parent',
        label: 'Criar Avaliação',
        icon: PlusCircle,
        children: [
          { id: 'create_assessment_wizard', label: 'Modo Fácil', icon: Sparkles, path: '/app/wizard' },
          { id: 'create_assessment', label: 'Modo Avançado', icon: PlusCircle, path: '/app/editor' }
        ]
      },
      { id: 'questions_parent', label: 'Banco de Questões', icon: Database, path: '/app/questions' },
      { id: 'assessment_bank', label: 'Banco de Avaliações', icon: Library, path: '/app/banco-avaliacoes' },
      { id: 'templates', label: 'Modelos Prontos', icon: Layout, path: '/app/templates', premium: true, adminOnly: true }
    ]
  },
  {
    id: 'operar',
    label: 'Operar',
    items: [
      { id: 'reports_usage', label: 'Relatórios de uso', icon: FileBarChart, path: '/app/reports', premium: true, adminOnly: true },
      { id: 'help', label: 'Ajuda', icon: HelpCircle, path: '/app/help', adminOnly: true },
      { id: 'settings', label: 'Configurações', icon: Settings, path: '/app/settings' },
      { id: 'subscription', label: 'Gerenciar Assinatura', icon: Crown, path: '/app/subscription-management' }
    ]
  }
]

/** Feature checada por rota premium; o resto cai em 'analytics' (comportamento de sempre) */
const PREMIUM_FEATURE_BY_PATH: Record<string, string> = {
  '/app/templates': 'premium_templates',
  '/app/reports': 'analytics'
}

/**
 * Regras de acesso/ativo/navegação da sidebar, compartilhadas pelo painel
 * expandido e pelo trilho. `onNavigate` roda após navegar (fecha drawer/peek).
 */
export function useSidebarNavigation(onNavigate: () => void) {
  const navigate = useNavigate()
  const location = useLocation()
  const { canAccess } = useSubscription()
  const { isAdmin } = useAuth()

  const isActive = (path?: string): boolean => {
    if (!path) return false
    if (path === '/app') return location.pathname === '/app' || location.pathname === '/app/'
    return location.pathname.startsWith(path)
  }

  const isItemActive = (item: SidebarItem): boolean =>
    isActive(item.path) || (item.children?.some(child => isActive(child.path)) ?? false)

  const isDisabledForNonAdmin = (item: SidebarItem): boolean => Boolean(item.adminOnly) && !isAdmin

  const hasAccess = (item: SidebarItem): boolean =>
    !item.premium || canAccess(PREMIUM_FEATURE_BY_PATH[item.path ?? ''] ?? 'analytics')

  const navigateTo = (path: string, premium?: boolean, adminOnly?: boolean): void => {
    if (adminOnly && !isAdmin) return
    if (premium && !canAccess(PREMIUM_FEATURE_BY_PATH[path] ?? 'analytics')) {
      navigate('/app/billing')
      onNavigate()
      return
    }
    navigate(path)
    onNavigate()
  }

  return { isActive, isItemActive, isDisabledForNonAdmin, hasAccess, navigateTo }
}
